import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import type { UserRole } from '@/lib/types';

interface RoleGuardProps {
  children: React.ReactNode;
  allowedRoles: UserRole[];
  fallbackPath?: string;
}

export function RoleGuard({ children, allowedRoles, fallbackPath = '/unauthorized' }: RoleGuardProps) {
  const { user } = useAuthStore();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!allowedRoles.includes(user.role)) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
}

// Convenience guards for specific roles
export function PatientGuard({ children }: { children: React.ReactNode }) {
  return (
    <RoleGuard allowedRoles={['PATIENT', 'PATIENT_REPRESENTATIVE']}>
      {children}
    </RoleGuard>
  );
}

export function PractitionerGuard({ children }: { children: React.ReactNode }) {
  return (
    <RoleGuard allowedRoles={['PRACTITIONER_ACTIVE', 'PRACTITIONER_PENDING']}>
      {children}
    </RoleGuard>
  );
}

export function SecretaryGuard({ children }: { children: React.ReactNode }) {
  return (
    <RoleGuard allowedRoles={['MEDICAL_SECRETARY']}>
      {children}
    </RoleGuard>
  );
}

export function AdminGuard({ children }: { children: React.ReactNode }) {
  return (
    <RoleGuard allowedRoles={['ADMIN', 'COMPLIANCE', 'SUPPORT']}>
      {children}
    </RoleGuard>
  );
}